// 与 touranki-server 的通信层：HTTP 接口 + WebSocket 实时通道。
//
// 注意事项：信封与消息格式以 touranki-server/docs/server.md 的 REST API / WebSocket 章节为准；这里不做任何规则判断，
// 只负责搬运（前端不需要懂规则）。

import type {
  GameEvent,
  LegalAction,
  Pending,
  Private,
  Room,
  Ruleset,
  ServerMessage,
  Session,
  State,
} from "./types.ts";

const API = "/api/v1";

export class ApiError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
  }
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  error?: { code: string; message: string };
}

async function request<T>(
  path: string,
  options: { method?: string; token?: string; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.token) headers["Authorization"] = `Bearer ${options.token}`;
  const response = await fetch(`${API}${path}`, {
    method: options.method ?? "GET",
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  let envelope: Envelope<T> | null = null;
  try {
    envelope = (await response.json()) as Envelope<T>;
  } catch {
    envelope = null;
  }
  if (!envelope || envelope.ok !== true || envelope.data === undefined) {
    throw new ApiError(
      envelope?.error?.code ?? `HTTP_${response.status}`,
      envelope?.error?.message ?? `HTTP ${response.status}`,
    );
  }
  return envelope.data;
}

export interface RoomStateData {
  room: Room;
  seq: number;
  state: State | null;
  private: Private | null;
  pending: Pending | null;
  events: GameEvent[] | null;
  /** 该收件人此刻可以提交的 action（原样回发即可）；空数组＝只能等待。 */
  legal_actions: LegalAction[];
  /** 正在处理 pending 询问的玩家（无则空串）。 */
  waiting_for?: string;
}

export const api = {
  createSession: (name: string, token?: string) =>
    request<Session>("/sessions", {
      method: "POST",
      body: { name },
      ...(token ? { token } : {}),
    }),

  listRooms: (token: string) => request<{ rooms: Room[] }>("/rooms", { token }),

  createRoom: (
    token: string,
    body: {
      name: string;
      max_players: number;
      is_public: boolean;
      ruleset: Ruleset;
      seed: number | null;
    },
  ) => request<Room>("/rooms", { method: "POST", token, body }),

  getRoom: (token: string, roomId: string) =>
    request<Room>(`/rooms/${roomId}`, { token }),

  joinRoom: (token: string, roomId: string) =>
    request<Room>(`/rooms/${roomId}/join`, { method: "POST", token }),

  setReady: (token: string, roomId: string, ready: boolean) =>
    request<Room>(`/rooms/${roomId}/ready`, {
      method: "POST",
      token,
      body: { ready },
    }),

  setBots: (token: string, roomId: string, count: number) =>
    request<Room>(`/rooms/${roomId}/bots`, {
      method: "PUT",
      token,
      body: { count },
    }),

  start: (token: string, roomId: string) =>
    request<Room>(`/rooms/${roomId}/start`, { method: "POST", token }),

  leave: (token: string, roomId: string) =>
    request<Room>(`/rooms/${roomId}/leave`, { method: "POST", token }),

  fetchState: (token: string, roomId: string, sinceSeq?: number) =>
    request<RoomStateData>(
      `/rooms/${roomId}/state${sinceSeq === undefined ? "" : `?since_seq=${sinceSeq}`}`,
      { token },
    ),
};

export type ConnectionStatus = "idle" | "connecting" | "online" | "offline";

export interface SocketHandlers {
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
  onUnauthorized: () => void;
}

/** WS 的默认心跳间隔与重连退避（见 touranki-server/docs/server.md 的 WebSocket 章节）。 */
const PING_INTERVAL_MS = 30_000;
const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 8_000;

/**
 * TableSocket 是实时通道的薄封装：鉴权握手、心跳、断线重连与 sync 补齐。
 *
 * 注意事项：重连后会自动发 `sync { since_seq }`，所以调用方只要按 seq 去重即可。
 */
export class TableSocket {
  private socket: WebSocket | null = null;
  private token = "";
  private sinceSeq = 0;
  private lastSeq = 0;
  private ping: number | undefined;
  private retry: number | undefined;
  private delay = RETRY_MIN_MS;
  private closed = true;
  private counter = 0;

  constructor(private readonly handlers: SocketHandlers) {}

  connect(token: string, lastSeq: number): void {
    this.token = token;
    this.lastSeq = lastSeq;
    this.closed = false;
    this.open();
  }

  close(): void {
    this.closed = true;
    window.clearTimeout(this.retry);
    this.stopPing();
    this.socket?.close();
    this.socket = null;
    this.handlers.onStatus("idle");
  }

  /** send 发送一条 action，返回请求 id（用于日志与排错）。 */
  send(action: Record<string, unknown>): string {
    const requestId = `c-${String(++this.counter).padStart(4, "0")}`;
    if (this.socket?.readyState !== WebSocket.OPEN) {
      throw new ApiError("OFFLINE", "实时通道未连接");
    }
    this.socket.send(JSON.stringify({ type: "action", request_id: requestId, action }));
    return requestId;
  }

  private open(): void {
    if (this.closed) return;
    // 换代：先把旧连接的引用摘掉再关它，旧连接的 close 事件就会被守卫忽略，
    // 不会误清掉新连接（StrictMode 下 connect() 会被调用两次）。
    const previous = this.socket;
    this.socket = null;
    previous?.close();
    this.handlers.onStatus("connecting");
    const scheme = location.protocol === "https:" ? "wss" : "ws";
    const socket = new WebSocket(`${scheme}://${location.host}${API}/ws`);
    this.socket = socket;

    socket.addEventListener("open", () => {
      socket.send(JSON.stringify({ type: "auth", token: this.token }));
    });

    socket.addEventListener("message", (raw) => {
      // 旧连接的迟到消息必须丢弃：connect() 会换 socket，关掉的旧连接仍可能投递事件。
      if (this.socket !== socket) return;
      let message: ServerMessage;
      try {
        message = JSON.parse(String(raw.data)) as ServerMessage;
      } catch {
        return;
      }
      if (message.type === "welcome") {
        this.delay = RETRY_MIN_MS;
        this.handlers.onStatus("online");
        if (this.lastSeq > 0) {
          socket.send(JSON.stringify({ type: "sync", since_seq: this.lastSeq }));
        }
        this.startPing();
      }
      if (message.type === "state") this.lastSeq = Math.max(this.lastSeq, message.seq);
      if (message.type === "events") this.lastSeq = Math.max(this.lastSeq, message.seq);
      this.handlers.onMessage(message);
    });

    socket.addEventListener("close", (event) => {
      // 关键：只有「当前」socket 的关闭才作数。connect() 换代后，旧 socket 的 close 事件
      // 若照旧执行，会把新 socket 的引用清空，导致此后所有 send 都报「实时通道未连接」。
      if (this.socket !== socket) return;
      this.stopPing();
      if (this.closed) return;
      this.socket = null;
      if (event.code === 4001) {
        this.closed = true;
        this.handlers.onStatus("idle");
        this.handlers.onUnauthorized();
        return;
      }
      this.handlers.onStatus("offline");
      this.retry = window.setTimeout(() => this.open(), this.delay);
      this.delay = Math.min(this.delay * 2, RETRY_MAX_MS);
    });

    socket.addEventListener("error", () => socket.close());
  }

  private startPing(): void {
    this.stopPing();
    this.ping = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: "ping", ts: new Date().toISOString() }));
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    window.clearInterval(this.ping);
    this.ping = undefined;
  }
}
